<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte/internal'
    import ZentitySelect from '../_common/z/zentity-select.svelte'
    import PaymentsLinkSelect from './payments-link-select-2.svelte'
    import CheckoutForm from '../public/checkout/checkout-form.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import ZContainerCol from '../_common/z/z-container-col.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import Fa from 'svelte-fa'
    import { faAngleLeft, faFileInvoiceDollar, faInfinity, faLink, faPaperPlane } from '@fortawesome/free-solid-svg-icons'
    import type { Person } from '../../modules/eventsApi/persons/person'
    import type { Gateway } from '../../modules/paymentsApi/gateways/gateway.entity'
    import type { PaymentLink } from '../../modules/paymentsApi/payment-links/payment-link.entity'
    import type { CreatePaymentLinkDto } from '../../modules/paymentsApi/payment-links/dto/create-payment-link.dto'
    import { PaymentMethods, PaymentMethodsFriendly } from '../../modules/paymentsApi/charges/charge.enum'
    import { PurchaseType, isSubscriptionType, SUBSCRIPTION_TYPES, CHARGE_TYPES } from '../../modules/paymentsApi/purchases/purchase.enum'
    import { SendMessageTemplate } from '../_common/send-message.template'
    import dayjs from 'dayjs'
    import ZinputBreak from '../_common/z/zinput-break.svelte'

    // ##############################################################################################################
    const dispatch = createEventDispatcher()

    // Dois eixos independentes, e por isso duas perguntas separadas.
    //
    // TIPO é o que muda a natureza do que se cria — à vista ou recorrente — e arrasta consequência
    // para tudo (intervalo, credencial reutilizável, ciclo de cancelamento). É a escolha da entrada.
    //
    // ORIGEM é só de onde vem a definição. Como não muda a natureza de nada, cabe num seletor ao
    // lado da ação. Antes os dois eixos estavam misturados numa escolha só de três opções, e por
    // isso "assinatura avulsa" não tinha lugar.
    type Tipo = 'cobranca' | 'assinatura'
    let tipo: Tipo = null
    let origem: 'avulso' | 'predefinido' = 'avulso'

    // Cobrar agora exige cliente (é ele que será cobrado). Gerar link não: quem preenche os dados
    // é o pagador, na tela pública — informar o cliente aqui só amarra o link a ele.
    let acao: 'cobrar' | 'link' = 'cobrar'

    let person: Person = null
    let paymentLink: PaymentLink = null

    let gateways: Gateway[] = []
    let gateway: Gateway = null

    // quantos cadastros existem de cada lado, para o seletor de origem começar no lugar útil
    let temCadastroCobranca = false
    let temCadastroAssinatura = false

    // o que se cobra no avulso
    let avulsa = {
        purchaseDescription: '',
        purchaseTotalValue: null as number,
        purchaseMethod: null as string,
        // Teto de parcelas do cartão. Nasce em 12 e não em 1: antes este campo só existia no link,
        // e 1 como padrão passaria a forçar à vista em toda cobrança avulsa no cartão.
        maxInstallments: 12,
        // Só na cobrança avulsa: à vista ou carnê. Assinatura é sempre contínua — o carnê divide um
        // total em vencimentos, o que faz dele venda fracionada, não assinatura.
        forma: PurchaseType.full_charge as PurchaseType,
        // periodicidade do carnê e da assinatura
        purchaseInterval: 1,
        // quantos vencimentos tem o carnê
        purchaseTotalInstallments: 12,
        // Entrada do carnê, atrás de um toggle: desligada, a divisão é a de sempre. A entrada é o
        // primeiro dos N vencimentos, não um a mais — a tela diz "entrada + 5 parcelas" com N = 6.
        comEntrada: false,
        purchaseEntryValue: null as number,
    }
    // o Zinput de data devolve um Date (e um Date inválido quando o campo é limpo), por isso o tipo aberto
    let validade: Date | string = ''

    let linkUrl = ''
    // o registro adHoc já criado nesta tela. guardado para trocar de cliente remontar a URL em vez
    // de criar um segundo link para a mesma cobrança
    let linkIdGerado = ''
    let gerando = false
    let show = false

    onMount(async () => {
        try {
            show = true
            gateways = await window.api.payments.gateways.getAll()
            if (gateways.length === 1) gateway = gateways[0]

            // A contagem usa a mesma fronteira das listas, para o padrão do seletor de origem não
            // discordar do que o operador vai encontrar lá dentro.
            const cadastros: PaymentLink[] = await window.api.payments.paymentLinks.getAll()
            temCadastroAssinatura = cadastros.some((el) => isSubscriptionType(el.purchaseType))
            temCadastroCobranca = cadastros.some((el) => !isSubscriptionType(el.purchaseType))
        } catch (error) {
            window.error(error.message || error)
        } finally {
            show = false
        }
    })

    function escolher(escolhido: Tipo) {
        tipo = escolhido
        acao = 'cobrar'
        // Adaptativo: começa no cadastro quando existe algum, senão no avulso. Assim ninguém abre a
        // tela numa lista vazia, e quem não usa cadastro nunca precisa mexer no seletor.
        origem = escolhido === 'cobranca' ? (temCadastroCobranca ? 'predefinido' : 'avulso') : temCadastroAssinatura ? 'predefinido' : 'avulso'
        limpar()
    }

    function voltar() {
        tipo = null
        limpar()
    }

    function limpar() {
        person = null
        paymentLink = null
        linkUrl = ''
        linkIdGerado = ''
        validade = ''
        avulsa = {
            purchaseDescription: '',
            purchaseTotalValue: null,
            purchaseMethod: null,
            maxInstallments: 12,
            forma: PurchaseType.full_charge,
            purchaseInterval: 1,
            purchaseTotalInstallments: 12,
            comEntrada: false,
            purchaseEntryValue: null,
        }
        aplicaValidadePadrao()
    }

    // trocar de origem invalida o que foi montado do outro lado
    function trocarOrigem() {
        paymentLink = null
        linkUrl = ''
        linkIdGerado = ''
        aplicaValidadePadrao()
    }

    // A assinatura avulsa em link é a única combinação em que o reuso costuma ser indesejado: o
    // preço foi negociado com uma pessoa, e cada pagamento abre uma recorrência perpétua nova.
    // Por isso ela nasce com validade; nas demais a validade segue opcional e vazia.
    function aplicaValidadePadrao() {
        // a checagem é local, e não pelo 'temValidade' reativo: esta função é chamada logo depois de
        // mexer em 'validade', e o valor reativo ainda não teria sido recalculado
        const jaTem = !!validade && dayjs(validade).isValid()
        if (tipo === 'assinatura' && origem === 'avulso' && acao === 'link') {
            if (!jaTem) validade = dayjs().add(10, 'day').toDate()
            return
        }
        validade = ''
    }

    // trocar de ação invalida o link já gerado: os dados podem mudar daqui para frente
    function trocarAcao() {
        linkUrl = ''
        linkIdGerado = ''
        aplicaValidadePadrao()
    }

    // meios que o gateway realmente implementa, calculados pela api a partir do adapter dele
    $: meiosDoGateway = gateway?.gatewayPaymentMethods || []
    $: if (avulsa.purchaseMethod && meiosDoGateway.length && !meiosDoGateway.includes(avulsa.purchaseMethod)) {
        avulsa.purchaseMethod = null
    }

    // opção vazia explícita: sem ela o select fica sem seleção visível enquanto o valor é null
    $: gatewayOptions = [{ value: null, label: '' }, ...gateways.map((item) => ({ value: item, label: item.label }))]
    $: meioOptions = [{ value: null, label: '' }, ...meiosDoGateway.map((method) => ({ value: method, label: PaymentMethodsFriendly[method] || method }))]

    const acaoOptions = [
        { value: 'cobrar', label: 'Cobrar agora' },
        { value: 'link', label: 'Gerar link' },
    ]
    const origemOptions = [
        { value: 'predefinido', label: 'Predefinido' },
        { value: 'avulso', label: 'Avulso' },
    ]

    // o teto de parcelas vira lista: o Zinput de número não tem min/max, e aqui o intervalo é fechado
    const parcelasOptions = Array.from({ length: 24 }, (_, index) => ({ value: index + 1, label: index + 1 + 'x' }))
    const intervaloOptions = [
        { value: 1, label: 'Mensal' },
        { value: 2, label: 'Bimestral' },
        { value: 3, label: 'Trimestral' },
        { value: 6, label: 'Semestral' },
        { value: 12, label: 'Anual' },
    ]

    // O que será criado. Assinatura é sempre a recorrente contínua; a cobrança pode ser à vista ou
    // carnê, conforme a forma escolhida.
    $: tipoDaCompra = tipo === 'assinatura' ? PurchaseType.recurring_continuous : avulsa.forma

    // Formas de cobrança que ESTE gateway aceita neste meio — a restrição costuma ser da
    // combinação, não do gateway inteiro (no Mercado Pago o pix faz recorrência e o cartão não).
    $: formaOptions = (() => {
        const aceitos = gateway?.gatewayPurchaseTypes?.[avulsa.purchaseMethod]
        const todas = [
            // 'Pagamento único' e não 'À vista': o full_charge cobre também o parcelado no cartão,
            // que é uma transação só — quem divide é o emissor. O carnê ao lado é outra coisa: N
            // cobranças datadas, cada uma podendo falhar por conta própria.
            { value: PurchaseType.full_charge, label: 'Pagamento único' },
            { value: PurchaseType.recurring_fixed, label: 'Carnê' },
        ]
        if (!aceitos) return todas
        return todas.filter((el) => aceitos.includes(el.value))
    })()

    // Uma forma que deixou de valer não pode seguir selecionada em silêncio.
    //
    // O ajuste é feito no evento, e não em bloco reativo: o bloco leria as opções (que dependem de
    // 'avulsa') para escrever em 'avulsa', fechando um ciclo que o Svelte recusa.
    function ajustaForma() {
        if (tipo !== 'cobranca') return
        if (formaOptions.length && !formaOptions.some((el) => el.value === avulsa.forma)) {
            avulsa.forma = formaOptions[0].value
        }
    }

    // o gateway precisa aceitar exatamente o que se pretende criar, pelo meio escolhido
    $: avulsaAceita = (() => {
        if (!gateway || !avulsa.purchaseMethod) return true
        const tipos = gateway.gatewayPurchaseTypes?.[avulsa.purchaseMethod]
        return !tipos || tipos.includes(tipoDaCompra)
    })()

    // Entrada só existe no carnê e só quando ligada. Vale como valor efetivo apenas se sobra algo
    // para os demais vencimentos — uma entrada igual ao total seria uma cobrança única disfarçada.
    $: entradaAtiva = tipoDaCompra === PurchaseType.recurring_fixed && avulsa.comEntrada
    $: entradaValida =
        !entradaAtiva || (avulsa.purchaseEntryValue > 0 && avulsa.purchaseEntryValue < avulsa.purchaseTotalValue && avulsa.purchaseTotalInstallments > 1)
    $: entradaDoAvulso = entradaAtiva && entradaValida ? avulsa.purchaseEntryValue : null

    // O texto segue a convenção de vitrine: a entrada é anunciada à parte, e o número que aparece é
    // o de parcelas DEPOIS dela. Por dentro continuam sendo N vencimentos, com o primeiro ajustado.
    $: resumoDaEntrada = (() => {
        if (!entradaDoAvulso) return ''
        const vencimentos = avulsa.purchaseTotalInstallments || 1
        const restante = (avulsa.purchaseTotalValue - entradaDoAvulso) / (vencimentos - 1)
        const dinheiro = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        return `Entrada de ${dinheiro(entradaDoAvulso)} + ${vencimentos - 1}x de ${dinheiro(restante)}`
    })()

    $: avulsaCompleta =
        !!gateway && !!avulsa.purchaseDescription?.trim() && avulsa.purchaseTotalValue > 0 && !!avulsa.purchaseMethod && avulsaAceita && entradaValida

    // o que se cobra já está definido?
    $: configurado = origem === 'avulso' ? avulsaCompleta : !!paymentLink?.paymentLinkId

    // cobrar agora depende do cliente; gerar link não
    $: prontoParaCobrar = configurado && !!person
    $: prontoParaLink = configurado

    // limpar o campo de data devolve um Date inválido, que é truthy — só vale o que dayjs aceita
    $: temValidade = !!validade && dayjs(validade).isValid()

    // O campo purchaseTotalInstallments significa coisas diferentes conforme o tipo: no à vista é o
    // teto de parcelas do cartão; na recorrente fixa é quantas vezes a assinatura cobra; na
    // contínua não se aplica.
    function parcelasDoLink(): number {
        if (tipoDaCompra === PurchaseType.recurring_fixed) return avulsa.purchaseTotalInstallments || 1
        if (tipoDaCompra === PurchaseType.recurring_continuous) return 1
        return avulsa.purchaseMethod === PaymentMethods.credit ? avulsa.maxInstallments || 1 : 1
    }

    function urlDoCheckout(linkId: string, quem: Person): string {
        const r = quem ? '&r=' + quem.personId : ''
        return window.api.utils.utils.domain() + '/public/checkout?id=' + linkId + r
    }

    // A URL é sempre derivada do link já gerado, nunca escrita à mão em cada ponto. Assim trocar o
    // cliente apenas recompõe o '&r=' — antes isso apagava a URL e obrigava a clicar de novo, o que
    // no avulso criava um segundo registro pagável para a mesma cobrança.
    $: if (acao === 'link') {
        linkUrl = linkIdGerado ? urlDoCheckout(linkIdGerado, person) : ''
    }

    // O que "gerar" faz muda conforme a origem: no avulso cria um registro de PaymentLink marcado
    // como adHoc — ele não aparece nos cadastros, mas existe, e é dele que o checkout público lê o
    // que cobrar; no predefinido o registro já existe e só se revela o endereço dele.
    //
    // O botão fica nos dois casos mesmo sendo dispensável no predefinido: é a mesma ação para quem
    // usa a tela, e vale mais um gesto único do que economizar um clique num dos lados.
    async function gerarLink() {
        try {
            gerando = true
            let linkId = paymentLink?.paymentLinkId
            if (origem === 'avulso') {
                const dto: CreatePaymentLinkDto = {
                    gatewayId: gateway.gatewayId,
                    purchaseDescription: avulsa.purchaseDescription.trim(),
                    purchaseQuantity: 1,
                    purchaseTotalValue: avulsa.purchaseTotalValue,
                    // no cartão à vista o teto de parcelas vem daqui; na recorrente fixa, o número
                    // de parcelas é a própria duração da assinatura
                    purchaseTotalInstallments: parcelasDoLink(),
                    purchaseEntryValue: entradaDoAvulso,
                    paymentMethodsAllowed: avulsa.purchaseMethod,
                    purchaseType: tipoDaCompra,
                    // 'category' espelha a fronteira, e ela mora em isSubscriptionType()
                    category: isSubscriptionType(tipoDaCompra) ? 'subscription' : 'link',
                    // periodicidade vale para a assinatura e para o carnê; à vista não tem ciclo
                    purchaseInterval: tipoDaCompra === PurchaseType.full_charge ? 1 : avulsa.purchaseInterval,
                    adHoc: true,
                    expiresAt: temValidade ? dayjs(validade).endOf('day').toDate() : undefined,
                }
                const criado: PaymentLink = await window.api.payments.paymentLinks.create(dto)
                window.api.users.statistics.tick('pay.link.create')
                linkId = criado?.paymentLinkId
            }
            if (!linkId) {
                window.error('Não foi possível gerar o link.')
                return
            }
            // o bloco reativo monta a URL a partir daqui
            linkIdGerado = linkId
        } catch (error) {
            window.error(error.message || error)
        } finally {
            gerando = false
        }
    }

    function enviar() {
        if (!linkUrl) return
        const caminho = linkUrl.substring(linkUrl.indexOf('/public/checkout'))
        window.message(SendMessageTemplate.share_link_02(caminho.replace(/^\//, ''), 'Link de pagamento', person?.personId))
    }

    const opcoes = [
        {
            tipo: 'cobranca' as Tipo,
            icon: faFileInvoiceDollar,
            titulo: 'Cobrança',
            texto: 'Cobrança única, à vista ou parcelada.',
        },
        {
            tipo: 'assinatura' as Tipo,
            icon: faInfinity,
            titulo: 'Assinatura',
            texto: 'Cobrança recorrente, sem prazo definido.',
        },
    ]
</script>

<div class="root">
    {#if !tipo}
        <!-- etapa 1: só a natureza do que se cria. de onde vem a definição é escolha lá dentro. -->
        <Zframe class="m-3" title="O que você quer emitir?">
            <div class="escolhas">
                {#each opcoes as opcao}
                    <button class="escolha" on:click={() => escolher(opcao.tipo)}>
                        <div class="escolha-icone"><Fa icon={opcao.icon} /></div>
                        <div class="escolha-titulo">{opcao.titulo}</div>
                        <div class="escolha-texto">{opcao.texto}</div>
                    </button>
                {/each}
            </div>
        </Zframe>
    {:else}
        <Zact>
            <Zbutton color="secondary" icon={faAngleLeft} label="Trocar" on:click={voltar} />
            <div class="spacer" />
            {#if acao === 'link'}
                <Zbutton
                    color="primary"
                    icon={faLink}
                    label={linkUrl ? 'Gerar novamente' : 'Gerar link'}
                    loading={gerando}
                    disabled={!prontoParaLink || gerando}
                    on:click={gerarLink}
                />
                {#if linkUrl}
                    <Zbutton color="green" icon={faPaperPlane} label="Enviar" on:click={enviar} />
                {/if}
            {/if}
        </Zact>

        <Zframe class="m-3" title={opcoes.find((el) => el.tipo === tipo)?.titulo}>
            <ZContainer gapRow="8px" gapCol="8px">
                <!-- os dois eixos, lado a lado: de onde vem a definição e o que fazer com ela -->
                <Zinput size={4} sizeM={12} type="inlineselect_2" options={origemOptions} bind:value={origem} on:change={trocarOrigem} />

                <ZinputBreak></ZinputBreak>
                <!-- o que se cobra -->
                {#if origem === 'avulso'}
                    <Zinput size={4} sizeM={12} label="Gateway" type="select" options={gatewayOptions} bind:value={gateway} on:change={ajustaForma} />
                    <!-- os meios vêm da api conforme o gateway; um meio novo aparece sozinho -->
                    <Zinput size={4} sizeM={12} label="Meio" type="select" options={meioOptions} bind:value={avulsa.purchaseMethod} on:change={ajustaForma} />

                    {#if tipo === 'cobranca'}
                        <!-- à vista ou carnê. as formas oferecidas dependem do gateway e do meio -->
                        <Zinput size={4} sizeM={12} label="Forma" type="select" options={formaOptions} bind:value={avulsa.forma} />
                        {#if avulsa.forma === PurchaseType.recurring_fixed}
                            <!-- carnê: o valor total é dividido nestes vencimentos -->
                            <Zinput
                                size={4}
                                sizeM={12}
                                label="Vencimentos"
                                type="select"
                                options={parcelasOptions}
                                bind:value={avulsa.purchaseTotalInstallments}
                            />
                            <Zinput size={4} sizeM={12} label="Periodicidade" type="select" options={intervaloOptions} bind:value={avulsa.purchaseInterval} />
                        {/if}
                    {:else}
                        <!-- assinatura é sempre contínua: cobra até alguém cancelar -->
                        <Zinput size={4} sizeM={12} label="Periodicidade" type="select" options={intervaloOptions} bind:value={avulsa.purchaseInterval} />
                    {/if}
                    {#if tipoDaCompra === PurchaseType.full_charge && avulsa.purchaseMethod === PaymentMethods.credit}
                        <!-- Mesmo campo do cadastro de cobranças, e vale nas duas ações: no link é o
                             pagador que escolhe o parcelamento, cobrando agora é o operador — nos dois
                             casos o teto é este, limitado ainda pelo que a bandeira permite. -->
                        <Zinput size={4} sizeM={12} label="Parcelamento máximo" type="select" options={parcelasOptions} bind:value={avulsa.maxInstallments} />
                    {/if}

                    <ZinputBreak></ZinputBreak>
                    <Zinput size={8} label="Descrição" type="text" bind:value={avulsa.purchaseDescription} />
                    <Zinput
                        size={4}
                        sizeM={12}
                        label={tipo === 'assinatura' ? 'Valor por período' : avulsa.forma === PurchaseType.recurring_fixed ? 'Valor total' : 'Valor'}
                        type="number"
                        bind:value={avulsa.purchaseTotalValue}
                    />
                    {#if tipoDaCompra === PurchaseType.recurring_fixed}
                        <!-- vem depois do valor total de propósito: a entrada é uma fatia dele -->
                        <Zinput size={4} sizeM={12} label="Pedir Entrada" type="toggle" bind:value={avulsa.comEntrada} />
                        {#if avulsa.comEntrada}
                            <Zinput size={4} sizeM={12} label="Valor da entrada" type="number" bind:value={avulsa.purchaseEntryValue} />
                        {/if}
                    {/if}
                {:else}
                    <ZContainerCol size={8} sizeM={12}>
                        <!-- a fronteira vem das constantes do enum, não de um critério local -->
                        <PaymentsLinkSelect
                            bind:paymentLink
                            purchaseTypes={tipo === 'assinatura' ? SUBSCRIPTION_TYPES : CHARGE_TYPES}
                            label={tipo === 'assinatura' ? 'Selecione uma assinatura' : 'Selecione uma cobrança'}
                            on:change={() => (linkIdGerado = '')}
                        />
                    </ZContainerCol>
                {/if}
                <ZinputBreak></ZinputBreak>
                <Zinput class="mt-2" size={4} sizeM={12} type="inlineselect_2" options={acaoOptions} bind:value={acao} on:change={trocarAcao} />
                <ZinputBreak></ZinputBreak>
                <!-- quem paga -->
                <ZContainerCol size={8}>
                    <ZentitySelect fonte="pessoa" bind:value={person} label={acao === 'cobrar' ? 'Cliente' : 'Cliente (opcional)'} />
                </ZContainerCol>

                {#if acao === 'link' && origem === 'avulso'}
                    <!-- validade só existe no avulso: um cadastro vive indefinidamente.
                         na assinatura avulsa ela já vem preenchida (ver aplicaValidadePadrao) -->
                    <Zinput size={4} sizeM={12} label={tipo === 'assinatura' ? 'Validade' : 'Validade (opcional)'} type="date" bind:value={validade} />
                {/if}

                {#if acao === 'link' && linkUrl}
                    <Zinput size={12} label="Link de pagamento" type="textarea" value={linkUrl} disabled={true} showCopy={true} />
                {/if}
            </ZContainer>

            {#if origem === 'avulso' && resumoDaEntrada}
                <div class="alert alert-secondary mt-3 mb-0">{resumoDaEntrada}</div>
            {/if}

            {#if origem === 'avulso' && !avulsaAceita}
                <div class="alert alert-warning mt-3 mb-0">
                    Este gateway não aceita {tipo === 'assinatura'
                        ? 'assinatura'
                        : avulsa.forma === PurchaseType.recurring_fixed
                          ? 'carnê'
                          : 'cobrança à vista'} por
                    {PaymentMethodsFriendly[avulsa.purchaseMethod] || avulsa.purchaseMethod}. Escolha outro meio ou outro gateway.
                </div>
            {/if}

            {#if acao === 'link'}
                {#if linkUrl && origem === 'avulso'}
                    <div class="alert alert-primary mt-3 mb-0">
                        O link pode ser pago mais de uma vez — cada pagamento gera {tipo === 'assinatura' ? 'uma assinatura nova' : 'uma cobrança nova'}.
                        {#if temValidade}
                            Ele expira em {dayjs(validade).format('DD/MM/YYYY')}.
                        {/if}
                    </div>
                {/if}
            {:else if prontoParaCobrar}
                <div class="mt-3">
                    <CheckoutForm
                        gatewayId={gateway?.gatewayId}
                        referenceId={person.personId}
                        paymentLinkId={paymentLink?.paymentLinkId}
                        purchaseDescription={avulsa.purchaseDescription}
                        purchaseTotalValue={avulsa.purchaseTotalValue}
                        purchaseType={tipoDaCompra}
                        purchaseMethod={avulsa.purchaseMethod}
                        maxInstallmentsAllowed={tipoDaCompra === PurchaseType.recurring_fixed ? avulsa.purchaseTotalInstallments : avulsa.maxInstallments}
                        entryValueAllowed={entradaDoAvulso}
                    />
                </div>
            {:else if configurado}
                <!-- <div class="alert alert-secondary mt-3 mb-0">Informe o cliente para cobrar agora.</div> -->
            {/if}
        </Zframe>
    {/if}

    <LoadSpinner {show} />
</div>

<style>
    .escolhas {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }
    .escolha {
        flex: 1 1 220px;
        min-width: 200px;
        text-align: left;
        padding: 16px;
        border-radius: 8px;
        border: 1px solid rgba(128, 128, 128, 0.35);
        background: transparent;
        color: inherit;
        cursor: pointer;
    }
    .escolha:hover {
        border-color: var(--bs-primary, #0d6efd);
        background-color: #ffffff12;
    }
    :global(.theme_light) .escolha:hover {
        background-color: #00000010;
    }
    .escolha-icone {
        font-size: 20px;
        margin-bottom: 8px;
        opacity: 0.75;
    }
    .escolha-titulo {
        font-weight: 600;
        margin-bottom: 4px;
    }
    .escolha-texto {
        font-size: 12px;
        opacity: 0.75;
    }
</style>
